import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { api, type LlmCreditSource, type SettingRow } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { KillSwitch } from "@/components/KillSwitch";
import { MoneyDelta } from "@/components/MoneyDelta";
import { Sparkline } from "@/components/charts/Sparkline";
import { usePositionTicks } from "@/hooks/usePositionTicks";
import { floatingTotal } from "@/lib/floating";
import { formatMoney } from "@/lib/format";
import { queryKeys } from "@/lib/queryClient";
import { isUnavailable, observationStart, sortPortfolios } from "@/routes/SelfTrader";
import { SELF_TRADER_PROBE, timeframeLabel } from "@/lib/selfTrader";

export function Dashboard() {
  // M31c — the socket is the live source now (~1s account merges, ~1s position ticks);
  // these polls are only the safety net for a socket that stalled without dying, so 60s.
  // Do not remove them: reconnect invalidates every root, a silent stall invalidates nothing.
  const account = useQuery({ queryKey: ["account"], queryFn: api.account, refetchInterval: 60_000 });
  const positions = useQuery({ queryKey: ["positions"], queryFn: api.positions, refetchInterval: 60_000 });
  const ticks = usePositionTicks();
  const settings = useQuery({ queryKey: ["settings"], queryFn: api.settings });

  const dryRun = settings.data?.settings.find((s: SettingRow) => s.key === "dry_run")?.value === true;

  return (
    <div className="space-y-4">
      <KillSwitch />

      {dryRun && (
        <div className="flex items-center gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm text-yellow-500">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Dry run is on — fills are simulated, no real orders are placed.
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Account</CardTitle>
        </CardHeader>
        <CardContent>
          {account.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : account.data && !account.data.live ? (
            <p className="text-sm text-muted-foreground">Broker not connected — {account.data.reason}.</p>
          ) : account.data?.live ? (
            <div className="grid grid-cols-2 gap-4">
              <Tile label="Balance" value={formatMoney(account.data.account.balance as number, (account.data.account.currency as string) ?? "USD")} />
              <Tile label="Equity" value={formatMoney(account.data.account.equity as number, (account.data.account.currency as string) ?? "USD")} />
              <Tile label="Margin" value={formatMoney(account.data.account.margin as number, (account.data.account.currency as string) ?? "USD")} />
              <Tile label="Free margin" value={formatMoney(account.data.account.freeMargin as number, (account.data.account.currency as string) ?? "USD")} />
            </div>
          ) : null}
        </CardContent>
      </Card>

      <TodayCard />

      <EquityCard />

      <CreditsCard />

      <SelfTraderCard />

      <Card>
        <CardHeader>
          <CardTitle>Open positions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {positions.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : positions.data && !positions.data.live ? (
            <p className="text-sm text-muted-foreground">Broker not connected — {positions.data.reason}.</p>
          ) : positions.data?.positions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open positions.</p>
          ) : (
            <>
              <div className="flex items-center justify-between border-b border-border pb-2 text-sm">
                <span className="text-muted-foreground">Floating P/L</span>
                <MoneyDelta value={floatingTotal(positions.data?.positions ?? [], ticks)} />
              </div>
              {positions.data?.positions.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 border-b border-border py-2 last:border-0">
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span className={p.type?.includes("SELL") ? "text-destructive" : "text-success"}>
                      {p.type?.includes("SELL") ? "SELL" : "BUY"}
                    </span>
                    <span className="font-mono">{p.symbol}</span>
                    {p.origin === "external" && (
                      <Badge variant="outline" className="text-xs">
                        external
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {p.signalId ? `Signal #${p.signalId}${p.slot ? ` · slot ${p.slot}` : ""}` : "Manual position"}
                  </div>
                </div>
                <MoneyDelta
                  value={(() => {
                    const tick = ticks.get(p.id);
                    return tick && !tick.stale && tick.profit !== undefined ? tick.profit : p.profit;
                  })()}
                />
              </div>
              ))}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * What has closed today, account-wide.
 *
 * Served by `/api/stats/overview`, which computes today live because the rollup that writes
 * `stats_daily` only runs at 22:10 UTC — so this card is deliberately labelled as still
 * counting rather than presented as a settled figure. Zero closed is a real answer and says
 * so; no money at all renders as a dash, because a dry-run day has none.
 */
function TodayCard() {
  const overview = useQuery({
    queryKey: queryKeys.statsOverview("daily"),
    queryFn: () => api.statsOverview("daily"),
    refetchInterval: 60_000,
  });

  const account = overview.data?.rows.find((row) => row.channelId === null);
  const decided = (account?.wins ?? 0) + (account?.losses ?? 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Today</CardTitle>
      </CardHeader>
      <CardContent>
        {overview.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : overview.isError ? (
          <p className="text-sm text-destructive">Could not load today's numbers.</p>
        ) : !account ? (
          <p className="text-sm text-muted-foreground">Nothing has closed today.</p>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <Tile label="Closed" value={String(account.signalsClosed)} />
            {/* The denominator travels with the rate everywhere in this app. */}
            <Tile
              label={`Won of ${decided}`}
              value={decided === 0 ? "—" : `${Math.round((account.wins / decided) * 100)}%`}
            />
            <Tile label="P/L" value={formatMoney(account.profitMoney, overview.data?.currency)} />
          </div>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Still counting — today is not rolled up until 22:10 UTC.
        </p>
        {/* The way into /reports. It is not in the bottom nav: a sixth tab does not fit a
            375px phone without shrinking every tap target, and the settled numbers belong
            next to the unsettled ones anyway. */}
        <Link
          to="/reports"
          className="mt-2 inline-block text-xs text-primary underline underline-offset-2"
        >
          Past reports — day, week and month →
        </Link>
      </CardContent>
    </Card>
  );
}

/**
 * M28a — the equity curve off `account_snapshots`. Drawdown numbers come from the SERVER,
 * computed over the full-resolution series — the drawn points are thinned to a drawing
 * budget and may well have dropped the very dip the number remembers.
 */
function EquityCard() {
  const equity = useQuery({ queryKey: queryKeys.equity(), queryFn: () => api.equity() });
  const data = equity.data;
  const money = (value: number) => formatMoney(value, data?.currency || "USD");

  return (
    <Card>
      <CardHeader>
        <CardTitle>Equity — last 30 days</CardTitle>
      </CardHeader>
      <CardContent>
        {equity.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : !data || data.points.length === 0 ? (
          // Pre-snapshot state: say so. A flat fabricated line would read as "nothing moved".
          <p className="text-sm text-muted-foreground">No equity history yet.</p>
        ) : (
          <>
            <Sparkline
              points={data.points
                .filter((point) => point.equity !== null)
                .map((point) => ({ t: point.t, v: point.equity! }))}
              ariaLabel={`Equity, last 30 days, now ${money(data.points.at(-1)?.equity ?? 0)}`}
              formatValue={money}
            />
            <div className="mt-2 flex justify-between text-xs text-muted-foreground">
              <span>
                max drawdown{" "}
                <span className="font-medium text-foreground">
                  {data.maxDrawdownMoney === null ? "—" : money(data.maxDrawdownMoney)}
                </span>
              </span>
              <span>
                off peak{" "}
                <span className="font-medium text-foreground">
                  {data.currentDrawdownFromPeak === null ? "—" : money(data.currentDrawdownFromPeak)}
                </span>
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * M15 — remaining credits per provider, refreshed each minute. An em-dash is an honest
 * unknown (billing API unreachable, or nothing configured to ask), never a zero.
 *
 * M36d — the model-provider tile has to say what its number *is*. The same "$9.17" means
 * three different things depending on where it came from: an OpenRouter account balance, a
 * spending cap set on one key, or an estimate derived from a figure Andy typed in. And
 * under BYOK even the account balance is not the pipeline's runway — it covers OpenRouter's
 * routing fee while the models bill to the OpenAI account behind the key. A tile that just
 * said "remaining" would be read as far more headroom than exists.
 */
const LLM_CREDIT_LABEL: Record<LlmCreditSource, string> = {
  "credits-api": "OpenRouter credits",
  "key-limit": "Key limit left",
  "metered-baseline": "OpenAI remaining (est.)",
  unknown: "Model credits",
};

function CreditsCard() {
  const credits = useQuery({ queryKey: queryKeys.credits, queryFn: api.credits, refetchInterval: 60_000 });

  const colorFor = (level: "ok" | "warn" | "alarm" | null) =>
    level === "alarm" ? "text-destructive" : level === "warn" ? "text-yellow-500" : "";
  const usd = (n: number | null) => (n === null ? "—" : `$${n.toFixed(2)}`);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Credits</CardTitle>
      </CardHeader>
      <CardContent>
        {credits.isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : credits.data ? (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">MetaApi remaining</div>
                <div className={`text-lg font-semibold ${colorFor(credits.data.levels.metaapi)}`}>
                  {usd(credits.data.metaapi.remainingUsd)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">
                  {LLM_CREDIT_LABEL[credits.data.llm.source]}
                </div>
                <div className={`text-lg font-semibold ${colorFor(credits.data.levels.llm)}`}>
                  {usd(credits.data.llm.remainingUsd)}
                </div>
              </div>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              model spend today ${credits.data.llm.spentTodayUsd.toFixed(2)}
              {credits.data.llm.source === "unknown" &&
                (credits.data.llm.provider === "openrouter" ? (
                  <> · set OPENROUTER_MANAGEMENT_API_KEY, or a spend limit on the key, to see remaining</>
                ) : (
                  <> · set an OpenAI baseline in Settings to see remaining</>
                ))}
            </div>
            {credits.data.llm.byok && (
              <div className="mt-1 text-xs text-muted-foreground">
                BYOK — that balance covers OpenRouter&rsquo;s fee; model cost bills to your OpenAI
                account.
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Could not load credits.</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * M41 — the way into /self-trader, for the same reason Reports is reached from the Today card:
 * a sixth nav tab does not fit a 375px phone. Renders nothing while the module is switched off,
 * so a server without it shows no dead card; "starting" while the module answers 503.
 */
function SelfTraderCard() {
  const health = useQuery({ queryKey: queryKeys.health, queryFn: api.health, refetchInterval: 30_000 });
  const probe = health.data?.checks[SELF_TRADER_PROBE];
  const enabled = probe !== undefined && probe.state !== "disabled";
  const status = useQuery({
    queryKey: queryKeys.selfTraderStatus,
    queryFn: api.selfTrader.status,
    refetchInterval: 30_000,
    enabled,
  });
  const portfolios = useQuery({
    queryKey: queryKeys.selfTraderPortfolios,
    queryFn: api.selfTrader.portfolios,
    refetchInterval: 30_000,
    enabled,
  });
  if (!enabled) return null;

  const data = portfolios.data;
  const best = data ? sortPortfolios(data.portfolios, "net", data.gates)[0] : undefined;
  const start = data ? observationStart(data.portfolios) : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle>Self-trader</CardTitle>
        {status.data && (
          <Badge variant={status.data.mode === "demo" ? "default" : "secondary"} className="uppercase">
            {status.data.mode}
          </Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        {isUnavailable(status.error) ? (
          <p className="text-muted-foreground">Starting…</p>
        ) : status.isError ? (
          <p className="text-destructive">Could not load the self-trader.</p>
        ) : status.isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {probe?.detail ?? probe?.state}
              {data && start !== null && ` · day ${Math.floor((Date.now() - start) / 86_400_000)} of ${data.gates.days}`}
            </p>
            {best && (
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs text-muted-foreground">
                  Best paper portfolio: {best.symbol} · {timeframeLabel(best.timeframe)} · {best.family}
                </span>
                <span className="shrink-0 whitespace-nowrap">
                  <MoneyDelta value={best.net} currency={data?.currency} />
                </span>
              </div>
            )}
          </>
        )}
        <Link to="/self-trader" className="inline-block text-xs text-primary underline underline-offset-2">
          Paper portfolios and promotion gates →
        </Link>
      </CardContent>
    </Card>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
